/**
 * MomentsCarousel — §03 single-moment deck.
 *
 * Mirrors `TensionsCarousel` with a four-tier hierarchy:
 *   1. Track name (small, dim)
 *   2. Big poster anchor — uppercase, shuffles in
 *   3. Body — the consequence (`pull`)
 *   4. Why line — small italic, dim
 *
 * Empty state shows a hint + begin pill + decorative dots. Active state
 * adds a counter, faint adjacent track names at the edges, and prev/next
 * controls. Keyboard ←/→ advance once started; per-moment animations are
 * gated by `prefers-reduced-motion: reduce` in CSS.
 *
 * Reset behavior matches the tensions carousel: parent `<DeepDive>` is
 * re-keyed on album swap, so this component remounts with index === null.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTrack } from '@/analytics';
import type { AdvanceMethod } from '@/analytics';
import type { SignatureMoment } from '@/types/album';

interface MomentsCarouselProps {
  moments: readonly SignatureMoment[];
}

export function MomentsCarousel({ moments }: MomentsCarouselProps): JSX.Element {
  const total = moments.length;
  const [index, setIndex] = useState<number | null>(null);
  const track = useTrack();

  const advance = useCallback(
    (to: number, method: AdvanceMethod) => {
      setIndex((prev) => {
        if (total === 0) return prev;
        if (prev === null) return prev;
        const safeTo = Math.min(Math.max(to, 0), total - 1);
        if (safeTo === prev) return prev;
        track('Moments Advance', { from: prev, to: safeTo, method });
        return safeTo;
      });
    },
    [track, total]
  );

  const begin = useCallback(() => {
    if (total === 0) return;
    track('Moments Begin', { count: total });
    setIndex(0);
  }, [track, total]);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && index < total - 1) {
        e.preventDefault();
        advance(index + 1, 'keyboard');
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        advance(index - 1, 'keyboard');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, advance]);

  if (total === 0) {
    return (
      <div className="dd-mn-frame is-empty" aria-label="Momentos clave">
        <div className="dd-mn-empty-eyebrow">— Sin momentos clave para este disco</div>
      </div>
    );
  }

  if (index === null) {
    return (
      <div className="dd-mn-frame is-empty" aria-label="Momentos clave — comenzar">
        <div className="dd-mn-empty-eyebrow">— Dos momentos · uno a la vez</div>
        <button
          type="button"
          className="dd-mn-begin"
          onClick={begin}
          aria-label="Empezar a leer los momentos"
        >
          <span className="prompt">Tap para ver el primer momento</span>
          <span className="arrow" aria-hidden="true">
            →
          </span>
        </button>
        <div className="dd-mn-dots" aria-hidden="true">
          {moments.map((_, i) => (
            <span key={i} className="dot" />
          ))}
        </div>
      </div>
    );
  }

  const safeIndex = Math.min(Math.max(index, 0), total - 1);
  const moment = moments[safeIndex];
  if (!moment) {
    return (
      <div className="dd-mn-frame is-empty" aria-label="Momentos clave">
        <div className="dd-mn-empty-eyebrow">— Sin momentos clave para este disco</div>
      </div>
    );
  }
  const prev = safeIndex > 0 ? (moments[safeIndex - 1] ?? null) : null;
  const next = safeIndex < total - 1 ? (moments[safeIndex + 1] ?? null) : null;

  return (
    <div className="dd-mn-frame">
      <div className="dd-mn-meta">
        <span className="counter">
          {String(safeIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
      </div>

      {prev && (
        <div className="dd-mn-edge dd-mn-edge-l" aria-hidden="true">
          {prev.track}
        </div>
      )}
      {next && (
        <div className="dd-mn-edge dd-mn-edge-r" aria-hidden="true">
          {next.track}
        </div>
      )}

      <div key={safeIndex} className="dd-mn-stage" aria-live="polite">
        <div className="dd-mn-track">{moment.track}</div>

        <h3 className="dd-mn-anchor">{moment.anchor}</h3>

        <p className="dd-mn-body">{moment.pull}</p>
        <p className="dd-mn-why">{moment.why}</p>
      </div>

      <div className="dd-mn-controls">
        <button
          type="button"
          className="dd-mn-btn"
          disabled={safeIndex === 0}
          onClick={() => advance(Math.max(0, safeIndex - 1), 'prev')}
          aria-label="Momento anterior"
        >
          <span className="arrow" aria-hidden="true">
            ←
          </span>
          <span className="label">Anterior</span>
        </button>

        <div className="dd-mn-dots" role="tablist" aria-label="Cambiar de momento">
          {moments.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`dot ${i === safeIndex ? 'is-active' : ''}`}
              role="tab"
              aria-label={`Ir al momento ${i + 1}`}
              aria-selected={i === safeIndex}
              onClick={() => advance(i, 'dot')}
            />
          ))}
        </div>

        <button
          type="button"
          className="dd-mn-btn"
          disabled={safeIndex === total - 1}
          onClick={() => advance(Math.min(total - 1, safeIndex + 1), 'next')}
          aria-label="Momento siguiente"
        >
          <span className="label">Siguiente</span>
          <span className="arrow" aria-hidden="true">
            →
          </span>
        </button>
      </div>
    </div>
  );
}
